import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  ISubscriptionRepository,
  Subscription,
} from '../../../interfaces/src';

interface SubscriptionRow {
  tenant_id: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  plan: 'lyfty_light';
  billing_cycle: 'monthly' | 'yearly';
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  payment_provider: 'flouci' | 'konnect' | null;
  payment_provider_customer_id: string | null;
  payment_provider_subscription_id: string | null;
  created_at: string;
  updated_at: string;
}

export class SupabaseSubscriptionRepository implements ISubscriptionRepository {
  constructor(private supabase: SupabaseClient) {}

  async getCurrent(): Promise<Subscription | null> {
    // The RLS policy on tenant_subscriptions filters to the caller's tenant,
    // so we don't need to pass tenant_id. `maybeSingle` so a missing row
    // returns null cleanly (the gate treats null as fail-open).
    const { data, error } = await this.supabase
      .from('tenant_subscriptions')
      .select('*')
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? mapSubscription(data as SubscriptionRow) : null;
  }

  async ensureForCurrent(trialDays: number): Promise<Subscription> {
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: SubscriptionRow | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'ensure_subscription_for_current_user',
      { p_trial_days: trialDays },
    );
    if (error) throw error;
    if (!data) throw new Error('ensure_subscription_for_current_user returned no row');
    return mapSubscription(data);
  }

  async cancelCurrent(): Promise<Subscription> {
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: SubscriptionRow | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbAny).rpc(
      'cancel_my_subscription',
      {},
    );
    if (error) throw error;
    if (!data) throw new Error('cancel_my_subscription returned no row');
    return mapSubscription(data);
  }
}

function mapSubscription(row: SubscriptionRow): Subscription {
  return {
    tenantId: row.tenant_id,
    status: row.status,
    plan: row.plan,
    billingCycle: row.billing_cycle,
    trialEndsAt: row.trial_ends_at,
    currentPeriodStart: row.current_period_start,
    currentPeriodEnd: row.current_period_end,
    canceledAt: row.canceled_at,
    paymentProvider: row.payment_provider,
    paymentProviderCustomerId: row.payment_provider_customer_id,
    paymentProviderSubscriptionId: row.payment_provider_subscription_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
